import React, { useEffect, useMemo, useState } from "react";
import { Calculator, Plus, RefreshCw, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

function eur(value) {
  return `${Number(value || 0).toFixed(2)} €`;
}

function unitPriceForQty(item, qty) {
  const q = Number(qty || 0);
  if ((item.price_rules || []).length > 0) {
    const rule = item.price_rules.find((r) => {
      const from = r.from ?? 0;
      const to = r.to ?? Infinity;
      return q >= from && q <= to && r.unit_price_eur !== undefined && r.unit_price_eur !== null;
    });
    if (rule) return Number(rule.unit_price_eur || 0);
  }
  return Number(item.price_eur || 0);
}

export default function AdminBudgets() {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState("");
  const [lines, setLines] = useState([]);

  const loadCatalog = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/service-catalog");
      setCatalog((data || []).filter((x) => x.visible_staff !== false && x.status !== "hidden"));
    } catch {
      toast.error("No se pudo cargar el catálogo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCatalog();
  }, []);

  const addLine = (item) => {
    const qty = 1;
    const unit = unitPriceForQty(item, qty);
    setLines((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        service_id: item.id,
        name: item.name,
        slug: item.slug,
        unit: item.unit || "unidad",
        pricing_type: item.pricing_type || "unit",
        qty,
        unit_price_eur: unit,
        manual_price: item.pricing_type === "manual",
        notes: "",
      },
    ]);
  };

  const updateLine = (lineId, patch) => {
    setLines((prev) => prev.map((line) => {
      if (line.id !== lineId) return line;
      const next = { ...line, ...patch };
      const item = catalog.find((x) => x.id === next.service_id);
      if (item && patch.qty !== undefined && !next.manual_price) {
        next.unit_price_eur = unitPriceForQty(item, next.qty);
      }
      return next;
    }));
  };

  const removeLine = (lineId) => {
    setLines((prev) => prev.filter((line) => line.id !== lineId));
  };

  const totals = useMemo(() => {
    const subtotal = lines.reduce((sum, line) => {
      return sum + Number(line.qty || 0) * Number(line.unit_price_eur || 0);
    }, 0);
    return { subtotal, total: subtotal };
  }, [lines]);

  const groupedCatalog = useMemo(() => {
    return catalog.reduce((acc, item) => {
      const key = item.category || "general";
      acc[key] = acc[key] || [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [catalog]);

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-black uppercase tracking-widest text-blue-800">
            <Calculator className="mr-1 h-4 w-4" />
            Presupuestos
          </div>
          <h1 className="mt-3 text-3xl font-black text-slate-900">Presupuestos KAPEI</h1>
          <p className="mt-1 max-w-3xl text-sm font-semibold text-slate-500">
            Constructor interno rápido. Añade servicios del catálogo, cambia cantidades y calcula total.
          </p>
        </div>

        <Button type="button" variant="outline" onClick={loadCatalog} disabled={loading}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Recargar catálogo
        </Button>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_420px]">
        <div className="space-y-5">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-black text-slate-900">Cliente / referencia</h2>
            <Input
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Nombre del cliente o referencia rápida"
              className="mt-3"
            />
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-lg font-black text-slate-900">Líneas del presupuesto</h2>
              <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-500">
                {lines.length} línea{lines.length === 1 ? "" : "s"}
              </div>
            </div>

            {lines.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 p-8 text-center">
                <div className="text-4xl">🧾</div>
                <div className="mt-2 text-sm font-black text-slate-700">Añade servicios desde el catálogo</div>
              </div>
            ) : (
              <div className="space-y-3">
                {lines.map((line) => (
                  <div key={line.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <div>
                        <div className="font-black text-slate-900">{line.name}</div>
                        <div className="text-xs font-bold text-slate-500">{line.unit}</div>
                      </div>
                      <Button type="button" variant="outline" onClick={() => removeLine(line.id)} className="text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="grid gap-2 md:grid-cols-[1fr_1fr_1fr]">
                      <Input
                        value={line.qty}
                        onChange={(e) => updateLine(line.id, { qty: e.target.value })}
                        placeholder="Cantidad"
                      />
                      <Input
                        value={line.unit_price_eur}
                        onChange={(e) => updateLine(line.id, { unit_price_eur: e.target.value, manual_price: true })}
                        placeholder="Precio unidad €"
                      />
                      <div className="rounded-md bg-white px-3 py-2 text-right font-black text-slate-900">
                        {eur(Number(line.qty || 0) * Number(line.unit_price_eur || 0))}
                      </div>
                    </div>

                    <Input
                      value={line.notes}
                      onChange={(e) => updateLine(line.id, { notes: e.target.value })}
                      placeholder="Nota opcional"
                      className="mt-2"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <aside className="space-y-5">
          <div className="rounded-3xl border border-lime-200 bg-lime-50 p-5 shadow-sm">
            <div className="text-xs font-black uppercase tracking-widest text-lime-700">Total presupuesto</div>
            <div className="mt-2 text-4xl font-black text-slate-900">{eur(totals.total)}</div>
            <div className="mt-2 text-xs font-bold text-lime-800">
              De momento cálculo interno. PDF, guardar presupuesto y Stripe vendrán después.
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="text-lg font-black text-slate-900">Catálogo</h2>
              {loading && <span className="text-xs font-bold text-slate-400">Cargando…</span>}
            </div>

            <div className="max-h-[68vh] space-y-4 overflow-y-auto pr-1">
              {Object.entries(groupedCatalog).map(([category, items]) => (
                <section key={category}>
                  <div className="mb-2 text-xs font-black uppercase tracking-widest text-slate-400">
                    {category}
                  </div>
                  <div className="space-y-2">
                    {items.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => addLine(item)}
                        className="flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-left hover:border-lime-300 hover:bg-lime-50"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm font-black text-slate-900">
                            {item.icon || "📦"} {item.name}
                          </div>
                          <div className="text-xs font-bold text-slate-500">
                            {item.pricing_type === "range" ? "Por rangos" : eur(item.price_eur)} · {item.unit || "unidad"}
                          </div>
                        </div>
                        <Plus className="h-4 w-4 shrink-0 text-lime-700" />
                      </button>
                    ))}
                  </div>
                </section>
              ))}

              {!loading && catalog.length === 0 && (
                <div className="rounded-2xl bg-slate-50 p-4 text-sm font-bold text-slate-500">
                  No hay servicios cargados. Ve a Catálogo Comercial y pulsa “Crear ejemplos”.
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
